/* =============================================================
   RATE CARDS — DATA ACCESS
   =============================================================
   `berth_rates`: a berth's price as an ordered list of rules.
   The first rule by `position` that fits the vessel and the stay
   prices the whole stay -- see
   supabase/migrations/20261004113019_create_berth_rates.sql.

   Order is the whole meaning of the card, so moving a rule is a
   first-class operation here, not a field on a form.
   ============================================================= */

import { supabase } from './supabase'
import { toDataError } from './errors'
import type { BerthRate, BerthRateInput, StayQuote } from './types'

const COLUMNS = 'id, berth_id, position, max_vessel_length_m, max_stay_hours, price_eur, per_hours'

export async function listRates(berthId: number): Promise<BerthRate[]> {
  const { data, error } = await supabase
    .from('berth_rates')
    .select(COLUMNS)
    .eq('berth_id', berthId)
    .order('position', { ascending: true })

  if (error) throw toDataError(error, 'Could not load the rate card.')
  return (data ?? []) as BerthRate[]
}

/** Appended at the end: a new rule is the least specific until moved up. */
export async function createRate(berthId: number, input: BerthRateInput): Promise<BerthRate> {
  const existing = await listRates(berthId)
  const position = existing.reduce((max, r) => Math.max(max, r.position), 0) + 1
  const { data, error } = await supabase
    .from('berth_rates')
    .insert({ berth_id: berthId, position, ...input })
    .select(COLUMNS)
    .single()

  if (error) throw toDataError(error, 'Could not add the rule.')
  return data as BerthRate
}

/** One rule, for the edit page. Null when the id does not exist. */
export async function getRate(id: number): Promise<BerthRate | null> {
  const { data, error } = await supabase
    .from('berth_rates')
    .select(COLUMNS)
    .eq('id', id)
    .maybeSingle()

  if (error) throw toDataError(error, 'Could not load the rule.')
  return data as BerthRate | null
}

export async function updateRate(id: number, input: BerthRateInput): Promise<BerthRate> {
  const { data, error } = await supabase
    .from('berth_rates')
    .update(input)
    .eq('id', id)
    .select(COLUMNS)
    .single()

  if (error) throw toDataError(error, 'Could not save the rule.')
  return data as BerthRate
}

export async function deleteRate(id: number): Promise<void> {
  const { error } = await supabase.from('berth_rates').delete().eq('id', id)
  if (error) throw toDataError(error, 'Could not delete the rule.')
}

/**
 * Swap two rules' positions.
 *
 * Three writes, because `(berth_id, position)` is unique and PostgREST sends
 * one statement per request: park `a` on a position nothing uses, move `b`
 * into `a`'s place, then `a` into `b`'s. A failure part-way leaves `a` parked
 * at 0 -- still unique, still ordered first, and fixed by moving it again.
 */
export async function swapRates(a: BerthRate, b: BerthRate): Promise<void> {
  const steps: Array<[number, number]> = [
    [a.id, 0],
    [b.id, a.position],
    [a.id, b.position],
  ]
  for (const [id, position] of steps) {
    const { error } = await supabase.from('berth_rates').update({ position }).eq('id', id)
    if (error) throw toDataError(error, 'Could not reorder the rules.')
  }
}

/** What a stay would cost, for the "try a price" box. Null = no rule fits. */
export async function quoteStay(
  berthId: number,
  vesselLengthM: number | null,
  arrival: string,
  departure: string,
): Promise<StayQuote | null> {
  const { data, error } = await supabase.rpc('stay_price', {
    p_berth_id: berthId,
    p_vessel_length_m: vesselLengthM,
    p_arrival: arrival,
    p_departure: departure,
  })

  if (error) throw toDataError(error, 'Could not price the stay.')
  const rows = (data ?? []) as StayQuote[]
  return rows[0] ?? null
}
